import React, { useState, useEffect, useContext } from 'react';
import { KeycloakContext } from '../App';
import { ShoppingCartIcon } from '@heroicons/react/24/outline';
import { API_URL } from '../runtimeConfig';
import { useToast } from '../providers/ToastProvider';
import { useConfirm } from '../providers/ConfirmProvider';
import { PageHeader } from '../components/ui/page-header';
import { Skeleton } from '../components/ui/skeleton';
import { formatDate, formatDateTime } from '../lib/format';

interface AdminOrder {
  id: string;
  user_id?: string;
  user_name?: string;
  type: string;
  offer_name?: string;
  offer_code?: string;
  price_ht: number;
  price_ttc: number;
  status: string;
  created_at: string;
  vcores?: number;
  ram_gb?: number;
  ssd_gb?: number;
  location?: string;
  os_mode?: string;
  os_family?: string;
  os_version?: string;
  os_app?: string;
  premium_backup?: boolean;
  snapshot_backup?: boolean;
  extra_storage_units?: number;
  local_part?: string;
  domain?: string;
  vps_order?: {
    ip_address?: string;
    username?: string;
    password?: string;
    protocol?: string;
    port?: number;
    os?: string;
    os_mode?: string;
    os_family?: string;
    os_version?: string;
    os_app?: string;
    location?: string;
    vcores?: number;
    ram_gb?: number;
    ssd_gb?: number;
    premium_backup?: boolean;
    snapshot_backup?: boolean;
    extra_storage_units?: number;
  };
  email_order?: {
    local_part?: string;
    password?: string;
  };
  database_order?: {
    engine?: string;
    offer_name?: string;
    offer_code?: string;
    storage_gb?: number;
  };
  domain_order?: {
    domain_name?: string;
    renewal_ht?: number;
    renewal_ttc?: number;
  };
  credits?: number;
  messaging_order?: {
    credits?: number;
  };
  emailing_credit_order?: {
    credits?: number;
  };
  whatsapp_credit_order?: {
    credits?: number;
  };
}

interface ValidateForm {
  ip_address: string;
  os: string;
  username: string;
  password: string;
  protocol: string;
  port: string;
  local_part: string;
  email_password: string;
  // Database - MySQL
  db_ip_address: string;
  db_port: string;
  db_username: string;
  db_password: string;
  db_database_name: string;
  // Database - MongoDB
  db_connection_string: string;
  db_cluster: string;
  // Database - access URL
  db_client_url: string;
  // Optional friendly label applied to the resulting VPS or Database resource.
  label: string;
}

const emptyValidateForm: ValidateForm = {
  ip_address: '', os: '', username: '', password: '', protocol: 'SSH', port: '22',
  local_part: '', email_password: '',
  db_ip_address: '', db_port: '3306', db_username: '', db_password: '', db_database_name: '',
  db_connection_string: '', db_cluster: '', db_client_url: '',
  label: '',
};

const LOCATION_LABELS: Record<string, string> = { fr: 'France', de: 'Germany', nl: 'Netherlands', uk: 'United Kingdom', pl: 'Poland', us: 'United States' };
const getLocationLabel = (loc?: string) => (loc ? (LOCATION_LABELS[loc] || loc) : 'Not specified');

export default function AdminOrders() {
  const ctx = useContext(KeycloakContext);
  const keycloak = ctx?.keycloak;
  const { toast } = useToast();
  const { confirm } = useConfirm();
  const [orders, setOrders] = useState<AdminOrder[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedOrder, setSelectedOrder] = useState<AdminOrder | null>(null);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [validateForm, setValidateForm] = useState<ValidateForm>(emptyValidateForm);
  const [saving, setSaving] = useState<boolean>(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<boolean>(false);
  const [usersMap, setUsersMap] = useState<Record<string, { username: string; email: string }>>({});

  useEffect(() => {
    fetchOrders();
    fetchUsers();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keycloak?.token]);

  const fetchUsers = async () => {
    try {
      const res = await fetch(`${API_URL}/admin/users`, { headers: { Authorization: `Bearer ${keycloak?.token}` } });
      if (!res.ok) return;
      const data = await res.json();
      const map: Record<string, { username: string; email: string }> = {};
      (data || []).forEach((u: any) => { map[u.id] = { username: u.username || u.preferred_username || u.id, email: u.email || '' }; });
      setUsersMap(map);
    } catch {}
  };

  const getUserDisplay = (userId: string) => {
    const u = usersMap[userId];
    if (!u) return userId;
    return u.email ? `${u.username} (${u.email})` : u.username;
  };

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/admin/orders`, { headers: { Authorization: `Bearer ${keycloak?.token}` } });
      if (!res.ok) throw new Error('Failed to fetch orders');
      const data = await res.json();
      setOrders(data || []);
      setError(null);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const openModal = (order: AdminOrder) => {
    setSelectedOrder(order);
    setFormError(null);
    setShowModal(true);
    if (order.type === 'email' && order.email_order) {
      setValidateForm((prev) => ({
        ...prev,
        local_part: order.email_order!.local_part || '',
        email_password: order.email_order!.password || '',
      }));
    } else if (order.type === 'database') {
      const defaultClientUrl = order.database_order?.engine === 'mysql'
        ? 'https://phpmyadmin.edj-labs.com'
        : `https://mongo.edj-labs.com/mongo-${order.user_id}-{id}/`;
      setValidateForm({ ...emptyValidateForm, db_client_url: defaultClientUrl });
    } else if (order.type === 'domain') {
      setValidateForm(emptyValidateForm);
    } else {
      const vpsOrder = order.vps_order || ({} as NonNullable<AdminOrder['vps_order']>);
      let osValue = '';
      if (vpsOrder.os_mode === 'os-app' && vpsOrder.os_app) {
        const apps: Record<string, string> = { 'debian-docker': 'Docker (Debian 12)', 'debian-n8n': 'n8n (Debian 12)' };
        osValue = apps[vpsOrder.os_app] || vpsOrder.os_app;
      } else if (vpsOrder.os_family && vpsOrder.os_version) {
        osValue = `${vpsOrder.os_family} ${vpsOrder.os_version}`;
      }
      let protocolValue = 'SSH';
      if (osValue.toLowerCase().includes('windows')) protocolValue = 'RDP';
      const portDefault = vpsOrder.port ? vpsOrder.port.toString() : (protocolValue === 'RDP' ? '3389' : '22');
      setValidateForm({
        ip_address: vpsOrder.ip_address || '',
        os: vpsOrder.os || osValue,
        username: vpsOrder.username || '',
        password: vpsOrder.password || '',
        protocol: vpsOrder.protocol || protocolValue,
        port: portDefault,
        local_part: '',
        email_password: '',
        db_ip_address: '', db_port: '3306', db_username: '', db_password: '', db_database_name: '',
        db_connection_string: '', db_cluster: '', db_client_url: '',
        label: '',
      });
    }
  };

  const closeModal = () => { setShowModal(false); setSelectedOrder(null); };

  const handleValidate = async () => {
    if (!selectedOrder) return;
    setFormError(null);
    setSaving(true);
    try {
      let payload: any;
      if (selectedOrder.type === 'email') {
        payload = { local_part: validateForm.local_part, password: validateForm.email_password };
      } else if (selectedOrder.type === 'database') {
        const engine = selectedOrder.database_order?.engine;
        const trimmedLabel = validateForm.label.trim();
        if (engine === 'mysql') {
          payload = {
            ip_address: validateForm.db_ip_address,
            port: parseInt(validateForm.db_port) || 3306,
            username: validateForm.db_username,
            password: validateForm.db_password,
            database_name: validateForm.db_database_name,
            client_url: validateForm.db_client_url || undefined,
            label: trimmedLabel || undefined,
          };
        } else {
          payload = {
            username: validateForm.db_username,
            password: validateForm.db_password,
            connection_string: validateForm.db_connection_string,
            cluster: validateForm.db_cluster,
            client_url: validateForm.db_client_url || undefined,
            label: trimmedLabel || undefined,
          };
        }
      } else if (selectedOrder.type === 'domain') {
        payload = {};
      } else {
        const trimmedLabel = validateForm.label.trim();
        payload = {
          ip_address: validateForm.ip_address,
          os: validateForm.os,
          username: validateForm.username,
          password: validateForm.password,
          protocol: validateForm.protocol,
          port: parseInt(validateForm.port) || 22,
          label: trimmedLabel || undefined,
        };
      }
      const url = `${API_URL}/admin/orders/${selectedOrder.type}/${selectedOrder.id}/fulfill`;
      const res = await fetch(url, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${keycloak?.token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) { const body = await res.json().catch(() => ({})); throw new Error(body.error || 'Failed to validate order'); }
      closeModal();
      await fetchOrders();
    } catch (err: any) {
      setFormError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedOrder) return;
    const ok = await confirm({ title: 'Delete order', message: `Delete order #${selectedOrder.id}? This action cannot be undone.`, confirmLabel: 'Delete', variant: 'danger' });
    if (!ok) return;
    setDeleting(true);
    try {
      const res = await fetch(`${API_URL}/admin/orders/${selectedOrder.type}/${selectedOrder.id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${keycloak?.token}` } });
      if (!res.ok) { const body = await res.json().catch(() => ({})); throw new Error(body.error || 'Failed to delete order'); }
      closeModal();
      await fetchOrders();
    } catch (err: any) {
      toast(err.message, 'error');
    } finally {
      setDeleting(false);
    }
  };

  const formatPrice = (value: number) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(value);

  if (loading) {
    return (
      <div className="space-y-6">
        <PageHeader
          icon={<ShoppingCartIcon />}
          title="Orders"
          description="Validate or manage customer orders."
        />
        <div className="rounded-lg border border-border bg-card p-6 space-y-3">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      </div>
    );
  }
  if (error) return <div className="rounded-md bg-destructive/10 p-4"><p className="text-sm text-destructive">Error: {error}</p></div>;

  return (
    <>
      <div className="space-y-6">
        <PageHeader
          icon={<ShoppingCartIcon />}
          title="Orders"
          description="Validate or manage customer orders."
        />

        <div className="bg-card shadow rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-border">
              <thead className="bg-muted/30">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">User</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Offer</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Price</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-card divide-y divide-border">
                {orders.length === 0 ? (
                  <tr><td colSpan={8} className="px-6 py-4 text-center text-sm text-muted-foreground">No orders found.</td></tr>
                ) : orders.map((order) => (
                  <tr key={order.id} className="hover:bg-muted/40">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground">#{order.id}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground">{order.user_name || order.user_id}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground uppercase">{order.type}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground">{order.offer_name || order.offer_code}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">{formatPrice(order.price_ttc)}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset ${order.status === 'validated' ? 'bg-success/10 text-success ring-success/20' : order.status === 'cancelled' ? 'bg-destructive/10 text-destructive ring-destructive/20' : 'bg-warning/10 text-warning ring-warning/20'}`}>{order.status.charAt(0).toUpperCase() + order.status.slice(1)}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">{formatDate(order.created_at)}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button type="button" onClick={() => openModal(order)} className="inline-flex items-center rounded-md border border-transparent px-3 py-1.5 text-xs font-medium text-white bg-primary hover:bg-primary/90 cursor-pointer">Manage</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {showModal && selectedOrder && (
          <div className="fixed inset-0 z-50 overflow-y-auto" role="dialog" aria-modal="true">
            <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              <div className="fixed inset-0 bg-background/70 backdrop-blur-sm" onClick={closeModal} />
              <div className="inline-block align-bottom bg-card rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
                <div className="bg-card px-4 pt-5 pb-4 sm:p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-semibold text-foreground">Order #{selectedOrder.id}</h3>
                    <button type="button" onClick={closeModal} className="text-muted-foreground hover:text-muted-foreground cursor-pointer">
                      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                  </div>
                  {formError && <div className="mb-4 rounded-md bg-destructive/10 p-3"><p className="text-sm text-red-700">{formError}</p></div>}

                  <div className="bg-muted/30 rounded-lg p-4 mb-4 text-sm">
                    <div className="grid grid-cols-2 gap-2">
                      <div className="col-span-2"><span className="font-medium">User:</span> {getUserDisplay(selectedOrder.user_id || '')}</div>
                      <div><span className="font-medium">Type:</span> {selectedOrder.type.toUpperCase()}</div>
                      <div><span className="font-medium">Offer:</span> {selectedOrder.offer_name || selectedOrder.offer_code}</div>
                      <div className="col-span-2"><span className="font-medium">Price:</span> {formatPrice(selectedOrder.price_ht)} excl. VAT / {formatPrice(selectedOrder.price_ttc)} inc. VAT</div>
                      <div><span className="font-medium">Status:</span> {selectedOrder.status.charAt(0).toUpperCase() + selectedOrder.status.slice(1)}</div>
                      <div><span className="font-medium">Date:</span> {formatDateTime(selectedOrder.created_at)}</div>
                    </div>
                    {selectedOrder.type === 'vps' && (
                      <div className="mt-2 pt-2 border-t border-border grid grid-cols-2 gap-2">
                        <div><span className="font-medium">Specs:</span> {selectedOrder.vps_order?.vcores} vCPU · {selectedOrder.vps_order?.ram_gb} GB RAM · {selectedOrder.vps_order?.ssd_gb} GB SSD</div>
                        <div><span className="font-medium">Location:</span> {getLocationLabel(selectedOrder.vps_order?.location)}</div>
                        <div><span className="font-medium">OS:</span> {selectedOrder.vps_order?.os_app
                          ? selectedOrder.vps_order.os_app
                          : (selectedOrder.vps_order?.os_family && selectedOrder.vps_order?.os_version
                            ? `${selectedOrder.vps_order.os_family} ${selectedOrder.vps_order.os_version}`
                            : 'Not specified')}</div>
                        {(selectedOrder.vps_order?.premium_backup || selectedOrder.vps_order?.snapshot_backup || (selectedOrder.vps_order?.extra_storage_units ?? 0) > 0) && (
                          <div className="col-span-2">
                            <span className="font-medium">Options:</span>
                            <ul className="mt-0.5 ml-2 text-muted-foreground text-xs space-y-0.5">
                              {selectedOrder.vps_order?.premium_backup && <li>• Automated premium backup</li>}
                              {selectedOrder.vps_order?.snapshot_backup && <li>• Snapshot backup</li>}
                              {(selectedOrder.vps_order?.extra_storage_units ?? 0) > 0 && (
                                <li>• Extra storage: {(selectedOrder.vps_order!.extra_storage_units!) * 50} GB</li>
                              )}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}
                    {selectedOrder.type === 'email' && (
                      <div className="mt-2 pt-2 border-t border-border">
                        <div><span className="font-medium">Email:</span> {selectedOrder.local_part ? `${selectedOrder.local_part}@${selectedOrder.domain}` : 'Pending'}</div>
                      </div>
                    )}
                    {selectedOrder.type === 'database' && (
                      <div className="mt-2 pt-2 border-t border-border grid grid-cols-2 gap-2">
                        <div><span className="font-medium">Engine:</span> {selectedOrder.database_order?.engine?.toUpperCase() || 'N/A'}</div>
                        <div><span className="font-medium">Storage:</span> {selectedOrder.database_order?.storage_gb === 0 ? '512 MB' : `${selectedOrder.database_order?.storage_gb} GB`}</div>
                      </div>
                    )}
                    {selectedOrder.type === 'domain' && (
                      <div className="mt-2 pt-2 border-t border-border grid grid-cols-2 gap-2">
                        <div className="col-span-2"><span className="font-medium">Domain:</span> {selectedOrder.domain_order?.domain_name}</div>
                        <div><span className="font-medium">1st year (excl. VAT):</span> {formatPrice(selectedOrder.price_ht)}</div>
                        <div><span className="font-medium">1st year (incl. VAT):</span> {formatPrice(selectedOrder.price_ttc)}</div>
                        <div><span className="font-medium">Renewal/yr (excl. VAT):</span> {formatPrice(selectedOrder.domain_order?.renewal_ht || 0)}</div>
                        <div><span className="font-medium">Renewal/yr (incl. VAT):</span> {formatPrice(selectedOrder.domain_order?.renewal_ttc || 0)}</div>
                      </div>
                    )}
                  </div>

                  {selectedOrder.status === 'pending' && (
                    <div>
                      <h4 className="text-sm font-semibold text-foreground mb-3">Fulfillment Details</h4>
                      {selectedOrder.type === 'email' ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-foreground">Email Local Part</label>
                            <input type="text" value={validateForm.local_part} onChange={(e) => setValidateForm({ ...validateForm, local_part: e.target.value })}
                              placeholder="e.g. john" className="mt-1 block w-full rounded-md border border-border shadow-sm px-3 py-2 text-sm focus-visible:ring-ring focus:border-primary" />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-foreground">Email Password</label>
                            <input type="text" value={validateForm.email_password} onChange={(e) => setValidateForm({ ...validateForm, email_password: e.target.value })}
                              className="mt-1 block w-full rounded-md border border-border shadow-sm px-3 py-2 text-sm focus-visible:ring-ring focus:border-primary" />
                          </div>
                        </div>
                      ) : selectedOrder.type === 'database' ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-foreground">Username</label>
                            <input type="text" value={validateForm.db_username} onChange={(e) => setValidateForm({ ...validateForm, db_username: e.target.value })}
                              className="mt-1 block w-full rounded-md border border-border shadow-sm px-3 py-2 text-sm focus-visible:ring-ring focus:border-primary" />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-foreground">Password</label>
                            <input type="password" value={validateForm.db_password} onChange={(e) => setValidateForm({ ...validateForm, db_password: e.target.value })}
                              className="mt-1 block w-full rounded-md border border-border shadow-sm px-3 py-2 text-sm focus-visible:ring-ring focus:border-primary" />
                          </div>
                          {selectedOrder.database_order?.engine === 'mysql' ? (
                            <>
                              <div>
                                <label className="block text-sm font-medium text-foreground">IP Address</label>
                                <input type="text" value={validateForm.db_ip_address} onChange={(e) => setValidateForm({ ...validateForm, db_ip_address: e.target.value })}
                                  placeholder="e.g. 1.2.3.4" className="mt-1 block w-full rounded-md border border-border shadow-sm px-3 py-2 text-sm focus-visible:ring-ring focus:border-primary" />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-foreground">Port</label>
                                <input type="text" value={validateForm.db_port} onChange={(e) => setValidateForm({ ...validateForm, db_port: e.target.value })}
                                  placeholder="e.g. 3306" className="mt-1 block w-full rounded-md border border-border shadow-sm px-3 py-2 text-sm focus-visible:ring-ring focus:border-primary" />
                              </div>
                              <div className="col-span-2">
                                <label className="block text-sm font-medium text-foreground">Database Name</label>
                                <input type="text" value={validateForm.db_database_name} onChange={(e) => setValidateForm({ ...validateForm, db_database_name: e.target.value })}
                                  className="mt-1 block w-full rounded-md border border-border shadow-sm px-3 py-2 text-sm focus-visible:ring-ring focus:border-primary" />
                              </div>
                            </>
                          ) : (
                            <>
                              <div>
                                <label className="block text-sm font-medium text-foreground">Cluster</label>
                                <input type="text" value={validateForm.db_cluster} onChange={(e) => setValidateForm({ ...validateForm, db_cluster: e.target.value })}
                                  placeholder="e.g. cluster0.abc.mongodb.net" className="mt-1 block w-full rounded-md border border-border shadow-sm px-3 py-2 text-sm focus-visible:ring-ring focus:border-primary" />
                              </div>
                              <div className="col-span-2">
                                <label className="block text-sm font-medium text-foreground">Connection String</label>
                                <input type="text" value={validateForm.db_connection_string} onChange={(e) => setValidateForm({ ...validateForm, db_connection_string: e.target.value })}
                                  placeholder="mongodb+srv://..." className="mt-1 block w-full rounded-md border border-border shadow-sm px-3 py-2 text-sm focus-visible:ring-ring focus:border-primary" />
                              </div>
                            </>
                          )}
                          <div className="col-span-2">
                            <label className="block text-sm font-medium text-foreground">Client URL</label>
                            <input type="text" value={validateForm.db_client_url} onChange={(e) => setValidateForm({ ...validateForm, db_client_url: e.target.value })}
                              placeholder={selectedOrder.database_order?.engine === 'mysql' ? 'https://phpmyadmin.edj-labs.com' : `https://mongo.edj-labs.com/mongo-${selectedOrder.user_id}-{id}/`}
                              className="mt-1 block w-full rounded-md border border-border shadow-sm px-3 py-2 text-sm focus-visible:ring-ring focus:border-primary" />
                            <p className="mt-1 text-xs text-muted-foreground">Leave blank to use the auto-generated default.</p>
                          </div>
                          <div className="col-span-2">
                            <label className="block text-sm font-medium text-foreground">Label <span className="text-muted-foreground font-normal">(optional)</span></label>
                            <input type="text" value={validateForm.label} maxLength={128} onChange={(e) => setValidateForm({ ...validateForm, label: e.target.value })}
                              placeholder="e.g. DB001"
                              className="mt-1 block w-full rounded-md border border-border shadow-sm px-3 py-2 text-sm focus-visible:ring-ring focus:border-primary" />
                            <p className="mt-1 text-xs text-muted-foreground">Friendly tag the user will see in their Services list. Editable later.</p>
                          </div>
                        </div>
                      ) : selectedOrder.type === 'domain' ? (
                        <div className="rounded-lg bg-blue-50 border border-blue-200 p-4">
                          <p className="text-sm text-blue-800">
                            This is a domain registration order for <strong>{selectedOrder.domain_order?.domain_name}</strong>.
                            Click <strong>Validate Order</strong> to confirm this domain has been purchased from OVH.
                          </p>
                        </div>
                      ) : selectedOrder.type === 'messaging' ? (
                        <div className="rounded-lg bg-primary/5 border border-primary/20 p-4">
                          <p className="text-sm text-foreground">
                            Clicking <strong>Validate Order</strong> will automatically credit <strong>{selectedOrder.messaging_order?.credits ?? selectedOrder.credits} messages credits</strong> to the user's messaging account.
                          </p>
                        </div>
                      ) : selectedOrder.type === 'emailing' ? (
                        <div className="rounded-lg bg-primary/5 border border-primary/20 p-4">
                          <p className="text-sm text-foreground">
                            Clicking <strong>Validate Order</strong> will automatically credit <strong>{selectedOrder.emailing_credit_order?.credits ?? selectedOrder.credits} email credits</strong> to the user's emailing account.
                          </p>
                        </div>
                      ) : selectedOrder.type === 'whatsapp' ? (
                        <div className="rounded-lg bg-primary/5 border border-primary/20 p-4">
                          <p className="text-sm text-foreground">
                            Clicking <strong>Validate Order</strong> will automatically credit <strong>{selectedOrder.whatsapp_credit_order?.credits ?? selectedOrder.credits} WhatsApp credits</strong> to the user's WhatsApp account.
                          </p>
                        </div>
                      ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-foreground">IP Address</label>
                            <input type="text" value={validateForm.ip_address} onChange={(e) => setValidateForm({ ...validateForm, ip_address: e.target.value })}
                              placeholder="e.g. 1.2.3.4" className="mt-1 block w-full rounded-md border border-border shadow-sm px-3 py-2 text-sm focus-visible:ring-ring focus:border-primary" />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-foreground">OS / Template</label>
                            <input type="text" value={validateForm.os} onChange={(e) => setValidateForm({ ...validateForm, os: e.target.value })}
                              className="mt-1 block w-full rounded-md border border-border shadow-sm px-3 py-2 text-sm focus-visible:ring-ring focus:border-primary" />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-foreground">Username</label>
                            <input type="text" value={validateForm.username} onChange={(e) => setValidateForm({ ...validateForm, username: e.target.value })}
                              className="mt-1 block w-full rounded-md border border-border shadow-sm px-3 py-2 text-sm focus-visible:ring-ring focus:border-primary" />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-foreground">Password</label>
                            <input type="password" value={validateForm.password} onChange={(e) => setValidateForm({ ...validateForm, password: e.target.value })}
                              className="mt-1 block w-full rounded-md border border-border shadow-sm px-3 py-2 text-sm focus-visible:ring-ring focus:border-primary" />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-foreground">Protocol</label>
                            <input type="text" value={validateForm.protocol} onChange={(e) => setValidateForm({ ...validateForm, protocol: e.target.value })}
                              placeholder="e.g. SSH or RDP"
                              className="mt-1 block w-full rounded-md border border-border shadow-sm px-3 py-2 text-sm focus-visible:ring-ring focus:border-primary" />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-foreground">Port</label>
                            <input type="number" value={validateForm.port} onChange={(e) => setValidateForm({ ...validateForm, port: e.target.value })}
                              placeholder="e.g. 22 or 3389"
                              className="mt-1 block w-full rounded-md border border-border shadow-sm px-3 py-2 text-sm focus-visible:ring-ring focus:border-primary" />
                          </div>
                          <div className="sm:col-span-2">
                            <label className="block text-sm font-medium text-foreground">Label <span className="text-muted-foreground font-normal">(optional)</span></label>
                            <input type="text" value={validateForm.label} maxLength={128} onChange={(e) => setValidateForm({ ...validateForm, label: e.target.value })}
                              placeholder="e.g. VPS001"
                              className="mt-1 block w-full rounded-md border border-border shadow-sm px-3 py-2 text-sm focus-visible:ring-ring focus:border-primary" />
                            <p className="mt-1 text-xs text-muted-foreground">Friendly tag the user will see in their Services list. Editable later.</p>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div className="bg-muted/30 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse gap-2">
                  {selectedOrder.status === 'pending' && (
                    <button type="button" onClick={handleValidate} disabled={saving || deleting}
                      className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary text-base font-medium text-white hover:bg-primary/90 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer">
                      {saving ? 'Validating…' : 'Validate Order'}
                    </button>
                  )}
                  <button type="button" onClick={handleDelete} disabled={saving || deleting}
                    className="w-full inline-flex justify-center rounded-md border border-destructive/40 shadow-sm px-4 py-2 bg-card text-base font-medium text-destructive hover:bg-destructive/10 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer">
                    {deleting ? 'Deleting…' : 'Delete Order'}
                  </button>
                  <button type="button" onClick={closeModal} disabled={saving || deleting}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-border shadow-sm px-4 py-2 bg-card text-base font-medium text-foreground hover:bg-muted/40 sm:mt-0 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer">
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
